import { Form, FormInstance } from 'antd';
import { useCreateDossierMutation } from 'hooks/dossiers.hooks';
import useFormSubmittable from 'hooks/useFormSubmittable';
import useMinimizeAnimation from 'hooks/useMinimizeAnimation';
import { useCallback, useState } from 'react';
import type { RefObject } from 'react';
import { CreateDossierLead } from 'types/Dossier';
import { mapDropzoneFiles } from 'utils/files';
import {
  BeneficiaryFormType,
  SiteContactInfo,
} from 'views/dossiers/ajout/forms/Beneficiary.form';
import { ProjectInformationsFormType } from 'views/dossiers/ajout/forms/ProjectInformations.form';
import {
  SiteAddressInformationsType,
  SiteContactInformationsType,
  SiteInformationsType,
} from 'views/dossiers/ajout/forms/SiteInformations.form';
import useAjoutDossierDraft from 'views/dossiers/ajout/hooks/useAjoutDossierDraft';

const NB_STEPS = 3;
const PROJECT_REQUIRED_FIELDS: (keyof ProjectInformationsFormType)[] = [
  'isAncien',
  'nature',
  'usage',
  'operation',
];

interface UseAjoutDossierFormControllerParams {
  openRequestId: number;
  onSuccess?: () => void;
}

interface UseAjoutDossierFormControllerReturn {
  state: {
    isHydrated: boolean;
    isCloseConfirmationOpen: boolean;
    isDraftActive: boolean;
    isDraftMinimized: boolean;
    currentSlide: number;
    isSiteStepComplete: boolean;
    isProjectStepSubmittable: boolean;
    isBeneficiaryStepSubmittable: boolean;
    siteFormUiResetKey: number;
    isCreateMutationLoading: boolean;
    isCreateMutationError: boolean;
    createMutationError?: Error | null;
  };
  ui: {
    isMinimizing: boolean;
    siteContactInfo?: SiteContactInfo;
    minimizeTargetRef: RefObject<HTMLDivElement | null>;
  };
  forms: {
    siteContactFormInstance: FormInstance<SiteContactInformationsType>;
    siteAddressFormInstance: FormInstance<SiteAddressInformationsType>;
    projectFormInstance: FormInstance<ProjectInformationsFormType>;
    beneficiaryFormInstance: FormInstance<BeneficiaryFormType>;
  };
  mutations: {
    createDossierMutation: ReturnType<typeof useCreateDossierMutation>;
  };
  actions: {
    requestMinimize: () => void;
    expandDraft: () => void;
    handleStepChange: (nextSlide: number) => void;
    handleCloseRequest: () => void;
    handleCancelCloseRequest: () => void;
    handleConfirmClose: () => void;
    handleCloseSuccessModal: () => void;
    buildCreateDossierPayload: () => Promise<CreateDossierLead | undefined>;
    validateProjectStep: () => Promise<boolean>;
    handleCreateDossierSuccess: () => void;
    handleSiteCompletionChange: (isComplete: boolean) => void;
    handleSiteValuesChange: (
      changedValues: Partial<SiteInformationsType>,
      allValues: SiteInformationsType,
    ) => void;
    handleProjectValuesChange: (
      changedValues: Partial<ProjectInformationsFormType>,
      allValues: ProjectInformationsFormType,
    ) => void;
    handleBeneficiaryValuesChange: (
      changedValues: Partial<BeneficiaryFormType>,
      allValues: BeneficiaryFormType,
    ) => void;
  };
}

export default function useAjoutDossierFormController({
  openRequestId,
  onSuccess,
}: UseAjoutDossierFormControllerParams): UseAjoutDossierFormControllerReturn {
  const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] = useState(false);
  const [siteFormUiResetKey, setSiteFormUiResetKey] = useState(0);

  const [siteContactFormInstance] = Form.useForm<SiteContactInformationsType>();
  const [siteAddressFormInstance] = Form.useForm<SiteAddressInformationsType>();
  const [projectFormInstance] = Form.useForm<ProjectInformationsFormType>();
  const [beneficiaryFormInstance] = Form.useForm<BeneficiaryFormType>();

  const createDossierMutation = useCreateDossierMutation();
  const { reset } = createDossierMutation;

  const resetForms = useCallback(() => {
    siteContactFormInstance.resetFields();
    siteAddressFormInstance.resetFields();
    projectFormInstance.resetFields();
    beneficiaryFormInstance.resetFields();
  }, [
    beneficiaryFormInstance,
    projectFormInstance,
    siteAddressFormInstance,
    siteContactFormInstance,
  ]);

  const {
    state: {
      isHydrated,
      isDraftActive,
      isDraftMinimized,
      currentSlide,
      isSiteStepComplete,
      siteContactInfo,
    },
    actions: {
      expandDraft,
      minimizeDraft,
      clearDraft,
      clearPersistedDraft,
      clearPersistedFiles,
      persistCurrentValues,
      updateCurrentSlide,
      handleSiteCompletionChange,
      handleSiteValuesChange,
      handleProjectValuesChange,
      handleBeneficiaryValuesChange,
    },
  } = useAjoutDossierDraft({
    openRequestId,
    siteContactFormInstance,
    siteAddressFormInstance,
    projectFormInstance,
    beneficiaryFormInstance,
    resetForms,
    resetMutationState: reset,
    onBeforeOpen: () => {
      reset();
    },
  });

  const isProjectFormSubmittable = useFormSubmittable(projectFormInstance, {
    fieldNames: PROJECT_REQUIRED_FIELDS,
    enabled: currentSlide === 1,
    initialSubmittable: false,
  });
  const isBeneficiaryFormSubmittable = useFormSubmittable(
    beneficiaryFormInstance,
    { enabled: currentSlide === 2, initialSubmittable: false },
  );

  const resetSiteStepCompletionState = useCallback(() => {
    // Reset local UI sections in SiteInformationsForm.
    setSiteFormUiResetKey((previous) => previous + 1);
  }, []);

  const {
    isMinimizing,
    requestMinimize,
    targetRef: minimizeTargetRef,
  } = useMinimizeAnimation({
    isMinimized: isDraftMinimized,
    sourceSelector: '.multi-step-modal .ant-modal-container',
    onBeforeMinimize: persistCurrentValues,
    onMinimize: minimizeDraft,
  });

  const handleStepChange = useCallback(
    (nextSlide: number) => {
      const clampedSlide = Math.max(0, Math.min(NB_STEPS - 1, nextSlide));
      persistCurrentValues();
      updateCurrentSlide(clampedSlide);
    },
    [persistCurrentValues, updateCurrentSlide],
  );

  const handleCloseRequest = useCallback(() => {
    persistCurrentValues();
    setIsCloseConfirmationOpen(true);
  }, [persistCurrentValues]);

  const handleCancelCloseRequest = useCallback(() => {
    setIsCloseConfirmationOpen(false);
  }, []);

  const handleConfirmClose = useCallback(() => {
    setIsCloseConfirmationOpen(false);
    clearPersistedFiles();
    clearPersistedDraft();
    clearDraft();
    // Reset UI et mutation ici pour garder la fermeture explicite (sans effet implicite).
    resetForms();
    resetSiteStepCompletionState();
    reset();
  }, [
    clearDraft,
    clearPersistedDraft,
    clearPersistedFiles,
    reset,
    resetForms,
    resetSiteStepCompletionState,
  ]);

  const buildCreateDossierPayload = useCallback(async () => {
    const beneficiaryValues = await beneficiaryFormInstance.validateFields();
    const currentStep1Values = {
      ...siteContactFormInstance.getFieldsValue(true),
      ...siteAddressFormInstance.getFieldsValue(true),
    } as SiteInformationsType;
    const currentProjectValues = projectFormInstance.getFieldsValue(
      true,
    ) as ProjectInformationsFormType;

    if (
      !beneficiaryValues ||
      !currentStep1Values.dateDebutTravaux ||
      !currentStep1Values.dateFinTravaux
    ) {
      return undefined;
    }

    const filesToSubmit =
      beneficiaryValues.files?.filter(
        (file) => file.originFileObj instanceof File,
      ) || [];
    const files = filesToSubmit.length
      ? await mapDropzoneFiles(filesToSubmit)
      : [];

    return {
      dossier: {
        referentCommercial: {
          email: currentStep1Values.interlocuteurChantierEmail,
          firstname: currentStep1Values.interlocuteurChantierFirstname,
          lastname: currentStep1Values.interlocuteurChantierLastname,
        },
        batiment: {
          ancien: currentProjectValues.isAncien === 'oui',
          ...(currentProjectValues.operation === 'Chauffage'
            ? {
                ancienneEnergieChauffage:
                  currentProjectValues.ancienneEnergieChauffage,
                nouvelleEnergieChauffage:
                  currentProjectValues.nouvelleEnergieChauffage,
                surfaceChauffee: currentProjectValues.surfaceChauffee,
                surfaceChauffeeRehabilitee:
                  currentProjectValues.surfaceChauffeeRehabilitee,
              }
            : currentProjectValues.operation === 'Ventilation'
              ? {
                  surfaceVentileeRehabilitee:
                    currentProjectValues.surfaceVentileeRehabilitee,
                }
              : {}),
        },
        chantier: {
          natureTravaux: currentProjectValues.nature,
          dateDebutTravaux: currentStep1Values.dateDebutTravaux.toISOString(),
          dateFinTravaux: currentStep1Values.dateFinTravaux.toISOString(),
        },
        beneficiaire: {
          civilite: beneficiaryValues.civilite,
          lastname: beneficiaryValues.lastname,
          firstname: beneficiaryValues.firstname,
          email: beneficiaryValues.email,
          telephone: beneficiaryValues.telephone,
          raisonSociale: currentStep1Values.raisonSocialeBeneficiaire,
        },
        site: {
          nom: currentStep1Values.nomSite,
          usage: currentProjectValues.usage,
          adresse: currentStep1Values.adresseSite,
          codePostal: currentStep1Values.codePostalSite,
          ville: currentStep1Values.villeSite,
        },
        description: beneficiaryValues.descriptionProjet,
        typeOperation: currentProjectValues.operation,
      },
      files,
    } satisfies CreateDossierLead;
  }, [
    beneficiaryFormInstance,
    projectFormInstance,
    siteAddressFormInstance,
    siteContactFormInstance,
  ]);

  const validateProjectStep = useCallback(async () => {
    try {
      await projectFormInstance.validateFields();
      return true;
    } catch {
      return false;
    }
  }, [projectFormInstance]);

  const handleCreateDossierSuccess = useCallback(() => {
    clearPersistedFiles();
    clearPersistedDraft();
    clearDraft();
    resetForms();
    resetSiteStepCompletionState();
    onSuccess?.();
  }, [
    clearDraft,
    clearPersistedDraft,
    clearPersistedFiles,
    onSuccess,
    resetForms,
    resetSiteStepCompletionState,
  ]);

  const handleCloseSuccessModal = useCallback(() => {
    resetSiteStepCompletionState();
    reset();
  }, [reset, resetSiteStepCompletionState]);

  return {
    state: {
      isHydrated,
      isCloseConfirmationOpen,
      isDraftActive,
      isDraftMinimized,
      currentSlide,
      isSiteStepComplete,
      isProjectStepSubmittable: isProjectFormSubmittable,
      isBeneficiaryStepSubmittable: isBeneficiaryFormSubmittable,
      siteFormUiResetKey,
      isCreateMutationLoading: createDossierMutation.isPending,
      isCreateMutationError: createDossierMutation.isError,
      createMutationError: createDossierMutation.error as Error | null,
    },
    ui: {
      isMinimizing,
      siteContactInfo,
      minimizeTargetRef,
    },
    forms: {
      siteContactFormInstance,
      siteAddressFormInstance,
      projectFormInstance,
      beneficiaryFormInstance,
    },
    mutations: {
      createDossierMutation,
    },
    actions: {
      requestMinimize,
      expandDraft,
      handleStepChange,
      handleCloseRequest,
      handleCancelCloseRequest,
      handleConfirmClose,
      handleCloseSuccessModal,
      buildCreateDossierPayload,
      validateProjectStep,
      handleCreateDossierSuccess,
      handleSiteCompletionChange,
      handleSiteValuesChange,
      handleProjectValuesChange,
      handleBeneficiaryValuesChange,
    },
  };
}
